import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, ListChecks } from 'lucide-react';
import { SUPPLIER_PAYMENT_MODE } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, dateOnly, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { formatMoneyInput } from '@/components/MoneyInput';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Badge, Button, Checkbox, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { overdueLabel } from './payables-view';
import {
  OPEN_GRN_PAGE_SIZE,
  Pager,
  parseMinor,
  supplierLabel,
  unallocatedWhenOldestFirst,
  useSuppliers,
  type AllocationDraft,
  type OpenGrn,
  type PaymentResult,
} from './PayablesPage';

const PAYMENTS_TAB = '/payables?tab=payments';

export function PaySupplierPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), PAYMENTS_TAB);
  const suppliers = useSuppliers();
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [supplierId, setSupplierId] = useState(params.get('supplierId') ?? '');
  const [mode, setMode] = useState('cash');
  const [amountText, setAmountText] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [oldestFirst, setOldestFirst] = useState(false);
  const [allocations, setAllocations] = useState<Record<string, AllocationDraft>>({});
  const [grnOffset, setGrnOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const openGrns = useQuery({
    queryKey: ['payables', 'open-grns', supplierId, grnOffset],
    queryFn: () =>
      get<OpenGrn[]>(`/pharmacy/payables/suppliers/${supplierId}/open-grns${qs({ limit: OPEN_GRN_PAGE_SIZE + 1, offset: grnOffset })}`),
    enabled: Boolean(supplierId),
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === supplierId ? previous : undefined),
  });

  const rows = (openGrns.data ?? []).slice(0, OPEN_GRN_PAGE_SIZE);
  const hasMoreGrns = (openGrns.data?.length ?? 0) > OPEN_GRN_PAGE_SIZE;

  const amountMinor = parseMinor(amountText);
  const allocationLines = Object.entries(allocations)
    .map(([grnId, draft]) => ({
      grnId,
      amountMinor: parseMinor(draft.text),
      outstandingMinor: draft.outstandingMinor,
    }))
    .filter((line) => line.amountMinor > 0);
  const allocatedMinor = allocationLines.reduce((sum, line) => sum + line.amountMinor, 0);
  const overAllocated = allocationLines.some((line) => line.amountMinor > line.outstandingMinor);
  const needsReference = mode === 'cheque' || mode === 'bank_transfer';

  const loadedOutstandingMinor = rows.reduce((sum, row) => sum + row.outstandingMinor, 0);
  const unallocatedMinor = oldestFirst
    ? unallocatedWhenOldestFirst(amountMinor, loadedOutstandingMinor, hasMoreGrns)
    : amountMinor - allocatedMinor;

  const problems: string[] = [];
  if (!supplierId) problems.push('a supplier');
  if (amountMinor <= 0) problems.push('an amount');
  if (needsReference && !reference.trim()) problems.push('a reference');
  if (!oldestFirst && allocatedMinor > amountMinor) problems.push('allocations that fit within the amount');
  if (!oldestFirst && overAllocated) problems.push('allocations within what each receipt still owes');

  const pay = useMutation({
    mutationFn: () =>
      post<PaymentResult>(
        '/pharmacy/payables/payments',
        {
          supplierId,
          mode,
          amountMinor,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
          allocations: oldestFirst ? [] : allocationLines.map((line) => ({ grnId: line.grnId, amountMinor: line.amountMinor })),
          allocateOldestFirst: oldestFirst,
        },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      if (result.status === 'pending_approval') {
        toast.warning(
          tr('pharmacy', 'waiting_for_approval'),
          tr('pharmacy', 'x_x_to_x_is_held_until_an_approver_releases_it', { number: result.number, amountMinor: money(result.amountMinor), supplierName: result.supplierName }),
        );
      } else {
        const onAccount = result.unallocatedMinor > 0 ? ` · ${money(result.unallocatedMinor)} on account` : '';
        toast.success(tr('pharmacy', 'supplier_paid'), tr('pharmacy', 'x_x_to_x_x', { number: result.number, amountMinor: money(result.amountMinor), supplierName: result.supplierName, onAccount: onAccount }));
      }
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const chooseSupplier = (id: string) => {
    setSupplierId(id);
    setAllocations({});
    setGrnOffset(0);
  };

  const setAllocation = (row: OpenGrn, text: string) =>
    setAllocations((current) => ({ ...current, [row.grnId]: { text, outstandingMinor: row.outstandingMinor } }));

  const fillAllocation = (row: OpenGrn) => {
    const elsewhereMinor = allocatedMinor - parseMinor(allocations[row.grnId]?.text ?? '');
    const roomMinor = amountMinor > 0 ? Math.max(0, amountMinor - elsewhereMinor) : row.outstandingMinor;
    const fillMinor = Math.min(row.outstandingMinor, roomMinor);
    setAllocation(row, fillMinor > 0 ? formatMoneyInput(fillMinor) : '');
  };

  const allocatedShown = unallocatedMinor === null ? 'Oldest first' : money(amountMinor - unallocatedMinor);
  const dirty = !pay.isSuccess && (amountText !== '' || reference !== '' || notes !== '' || allocationLines.length > 0 || mode !== 'cash');

  return (
    <FormScreen
      title={tr('pharmacy', 'pay_supplier')}
      description={tr('pharmacy', 'choose_which_goods_received_the_payment_settles')}
      eyebrow={tr('pharmacy', 'supplier_payables')}
      icon={Banknote}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (problems.length > 0) return;
        setError(null);
        pay.mutate();
      }}
    >
      <FormSection title={tr('pharmacy', 'payment')} icon={Banknote}>
        <Field
          label={tr('pharmacy', 'supplier')}
          required
          hint={suppliers.data && suppliers.data.length === 0 ? tr('pharmacy', 'none_yet_add_one_on_the_suppliers_tab_of') : undefined}
        >
          <Select value={supplierId} onChange={(event) => chooseSupplier(event.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {suppliers.data?.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplierLabel(supplier)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'mode')} required>
          <ChoiceButtons
            layout="chips"
            value={mode}
            onChange={setMode}
            options={SUPPLIER_PAYMENT_MODE.map((known) => ({ value: known, label: humanize(known, 'payment_mode') }))}
          />
        </Field>
        <Field label={tr('pharmacy', 'amount')} required>
          <Input type="text" dir="ltr" inputMode="decimal" className="tabular" value={amountText} onChange={(event) => setAmountText(event.target.value)} />
        </Field>
        <Field
          label={tr('pharmacy', 'reference')}
          required={needsReference}
          hint={needsReference ? tr('pharmacy', 'the_cheque_number_or_transfer_reference') : tr('pharmacy', 'optional')}
        >
          <Input value={reference} maxLength={120} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label={tr('pharmacy', 'notes')} className="sm:col-span-2">
          <Textarea rows={2} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'goods_received_still_owed')} icon={ListChecks} columns={1}>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Checkbox
            checked={oldestFirst}
            onCheckedChange={(checked) => {
              setOldestFirst(checked);
              setGrnOffset(0);
            }}
            label={tr('pharmacy', 'pay_oldest_first')}
          />
        </div>

        {!supplierId ? (
          <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">{tr('pharmacy', 'choose_a_supplier_to_see_the_goods_received')}</div>
        ) : (
          <>
            <DataTable
              dense
              rows={rows}
              loading={openGrns.isLoading} refreshing={openGrns.isFetching}
              error={openGrns.error}
              rowKey={(row) => row.grnId}
              empty={tr('pharmacy', 'nothing_is_outstanding_for_this_supplier_a')}
              columns={[
                {
                  header: tr('pharmacy', 'goods_received'),
                  cell: (row) => (
                    <div>
                      <p className="font-mono text-xs">{row.number}</p>
                      <p className="text-xs text-muted-foreground">
                        {[dateOnly(row.receivedAt), row.supplierInvoiceNo ? `invoice ${row.supplierInvoiceNo}` : null].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  ),
                },
                {
                  header: tr('pharmacy', 'due'),
                  cell: (row) => {
                    const late = overdueLabel(row.daysOverdue);
                    return (
                      <div>
                        <p className="text-xs">{row.dueDate ? dateOnly(row.dueDate) : '—'}</p>
                        {late && <Badge variant="destructive">{late}</Badge>}
                      </div>
                    );
                  },
                },
                { header: tr('pharmacy', 'total'), align: 'right', cell: (row) => money(row.totalMinor) },
                { header: tr('pharmacy', 'paid'), align: 'right', cell: (row) => money(row.paidMinor) },
                { header: tr('pharmacy', 'returned'), align: 'right', cell: (row) => money(row.returnedMinor) },
                { header: tr('pharmacy', 'outstanding'), align: 'right', cell: (row) => <strong>{money(row.outstandingMinor)}</strong> },
                {
                  header: tr('pharmacy', 'pay_now'),
                  align: 'right',
                  width: '190px',
                  cell: (row) => {
                    const text = allocations[row.grnId]?.text ?? '';
                    const tooMuch = parseMinor(text) > row.outstandingMinor;
                    return (
                      <div className="flex items-center justify-end gap-1">
                        <Input
                          type="text"
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={tr('pharmacy', 'amount_to_pay_on_x', { number: row.number })}
                          aria-invalid={tooMuch}
                          className={cn('h-8 w-28 text-end tabular', tooMuch && 'border-destructive')}
                          disabled={oldestFirst}
                          value={oldestFirst ? '' : text}
                          onChange={(event) => setAllocation(row, event.target.value)}
                        />
                        <Button type="button" size="sm" variant="ghost" disabled={oldestFirst} onClick={() => fillAllocation(row)}>
                          {tr('pharmacy', 'full')}
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
            />
            {!oldestFirst && (
              <Pager offset={grnOffset} pageSize={OPEN_GRN_PAGE_SIZE} shown={rows.length} hasNext={hasMoreGrns} busy={openGrns.isFetching} onChange={setGrnOffset} />
            )}
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span>
            {tr('pharmacy', 'payment')} <strong className="tabular">{money(amountMinor)}</strong>
          </span>
          <span>
            {tr('pharmacy', 'allocated')} <strong className="tabular">{allocatedShown}</strong>
          </span>
          <span>
            {tr('pharmacy', 'unallocated_on_account')}{' '}
            <strong className={cn('tabular', unallocatedMinor !== null && unallocatedMinor < 0 && 'text-destructive')}>
              {unallocatedMinor === null ? '—' : money(unallocatedMinor)}
            </strong>
          </span>
        </div>
        <p className="text-xs text-muted-foreground">{tr('pharmacy', 'a_payment_above_the_approval_limit_is_held')}</p>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={tr('pharmacy', 'pay_supplier')}
        loading={pay.isPending}
        incomplete={problems.length > 0}
        cancelTo={returnTo}
        note={problems.length > 0 ? `${tr('pharmacy', 'still_needed')} ${problems.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
